import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static values = {
    datasets: Array, // Cada item deve poder ter: { type, label, data, labels?, yAxisID? }
  }

  static targets = ["buttons"]

  static defaultColors = [
    "#EF4444", "#F97316", "#F59E0B", "#EAB308",
    "#84CC16", "#22C55E", "#10B981", "#14B8A6",
    "#06B6D4", "#0EA5E9", "#3B82F6", "#6366F1",
    "#8B5CF6", "#A855F7"
  ]

  connect() {
    this.visibleSet = new Set()
    this.buttonMap = new Map()
    this.allDatasets = []

    // === Configuração de eixos (ajuste as unidades/títulos/posições conforme necessário) ===
    this.axisMeta = {
      "1": { title: "Generation", unit: "kWh", position: "left" },
      "2": { title: "Wind speed", unit: "m/s", position: "right" },
      "3": { title: "Active Power", unit: "kW", position: "left" },
      "4": { title: "Reactive Power", unit: "kVAr", position: "right" }
    }

    try {
      this.datasets = this.datasetsValue || []
    } catch (e) {
      console.error("Erro ao parsear datasetsValue:", e)
      return
    }

    const canvas = this.element.querySelector("#operationalChart")
    if (!canvas) {
      console.error("Canvas com ID #operationalChart não encontrado.")
      return
    }

    const initialLabels = Array.isArray(this.datasets) && this.datasets[0] && Array.isArray(this.datasets[0].labels)
      ? this.datasets[0].labels
      : []

    // Cria closures para usar nas callbacks do Chart.js
    const axisMeta = this.axisMeta
    const numberFormatter = (v) => this.formatNumber(v)

    this.chart = new Chart(canvas.getContext("2d"), {
      type: "bar",
      data: {
        labels: initialLabels,
        datasets: []
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              // Adiciona unidade baseada no yAxisID do dataset
              label: function (context) {
                const dsLabel = context.dataset?.label || ""
                const yid = context.dataset?.yAxisID || "1"
                const unit = axisMeta[yid]?.unit || ""
                const raw = context.raw
                const val = numberFormatter(raw)
                return unit ? `${dsLabel}: ${val} ${unit}` : `${dsLabel}: ${val}`
              }
            }
          },
          title: { display: false }
        },
        // === Várias escalas (uma por yAxisID) ===
        // As chaves aqui devem COINCIDIR com o yAxisID enviado no dataset (ex.: "1","2","3","4")
        scales: {
          "1": {
            type: "linear",
            position: axisMeta["1"]?.position || "left",
            title: { display: true, text: `${axisMeta["1"]?.title || "Axis 1"} (${axisMeta["1"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["1"]?.unit ? `${v} ${axisMeta["1"].unit}` : v
              }
            }
          },
          "2": {
            type: "linear",
            position: axisMeta["2"]?.position || "right",
            grid: { drawOnChartArea: false }, // evita poluição visual com múltiplos grids
            title: { display: true, text: `${axisMeta["2"]?.title || "Axis 2"} (${axisMeta["2"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["2"]?.unit ? `${v} ${axisMeta["2"].unit}` : v
              }
            }
          },
          "3": {
            type: "linear",
            position: axisMeta["3"]?.position || "left",
            grid: { drawOnChartArea: true },
            title: { display: true, text: `${axisMeta["3"]?.title || "Axis 3"} (${axisMeta["3"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["3"]?.unit ? `${v} ${axisMeta["3"].unit}` : v
              }
            }
          },
          "4": {
            type: "linear",
            position: axisMeta["4"]?.position || "right",
            grid: { drawOnChartArea: false },
            title: { display: true, text: `${axisMeta["4"]?.title || "Axis 4"} (${axisMeta["4"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["4"]?.unit ? `${v} ${axisMeta["4"].unit}` : v
              }
            }
          },
          x: {
            type: "category",
            title: { display: false }
          }
        }
      }
    })

    this.loadDatasets()
  }

  loadDatasets() {
    const colors = this.constructor.defaultColors
    const valid = (this.datasets || []).filter(d =>
      d && d.type && d.label && Array.isArray(d.data)
    )

    if (valid.length === 0) {
      console.error("Nenhum dataset válido foi encontrado.")
      return
    }

    // Normaliza datasets, aplicando cores e garantindo yAxisID (default "1" se não vier)
    this.allDatasets = valid.map((item, index) => {
      const color = colors[index % colors.length]
      const yAxisID = item.yAxisID ? String(item.yAxisID) : "1"

      if (item.type === "bar") {
        return {
          ...item,
          type: "bar",
          yAxisID,
          backgroundColor: item.backgroundColor || color,
          borderColor: item.borderColor || color,
          order: valid.length - index
        }
      } else if (item.type === "line") {
        return {
          ...item,
          type: "line",
          yAxisID,
          borderColor: item.borderColor || color,
          pointBackgroundColor: item.pointBackgroundColor || color,
          backgroundColor: item.backgroundColor || `${color}33`,
          fill: false,
          tension: 0.3,
          order: valid.length - index
        }
      }
      return { ...item, yAxisID, order: valid.length - index }
    })

    // Labels do gráfico (usa labels do 1º dataset se existir)
    if (Array.isArray(this.allDatasets[0].labels)) {
      this.chart.data.labels = this.allDatasets[0].labels
    }

    // === Default: SOMENTE o primeiro dataset visível ===
    this.chart.data.datasets = [ this.allDatasets[0] ]
    this.visibleSet.add(this.allDatasets[0].label)

    // Botões de toggle para TODOS os datasets (bar e line), incluindo o primeiro
    this.buttonsTarget.innerHTML = ""
    this.allDatasets.forEach((ds) => {
      const li = document.createElement("li")
      li.textContent = ds.label
      li.dataset.label = ds.label
      li.className = this.buttonClass(this.visibleSet.has(ds.label))
      li.addEventListener("click", () => this.toggleDataset(ds, li))
      this.buttonsTarget.appendChild(li)
      this.buttonMap.set(ds.label, li)
    })

    this.updateAxesVisibility()
    this.chart.update()
  }

  toggleDataset(dataset, buttonEl) {
    const existsIndex = this.chart.data.datasets.findIndex(d => d.label === dataset.label)

    if (existsIndex > -1) {
      // Remove do gráfico
      this.chart.data.datasets.splice(existsIndex, 1)
      this.visibleSet.delete(dataset.label)
    } else {
      // Adiciona ao gráfico
      this.chart.data.datasets.push(dataset)
      this.visibleSet.add(dataset.label)
    }

    // Atualiza estilo do botão
    if (buttonEl) {
      buttonEl.className = this.buttonClass(this.visibleSet.has(dataset.label))
    } else {
      const el = this.buttonMap.get(dataset.label)
      if (el) el.className = this.buttonClass(this.visibleSet.has(dataset.label))
    }
    
    this.updateAxesVisibility()

    this.chart.update()
  }
  // Mostra o eixo apenas se houver ao menos 1 dataset visível usando esse yAxisID
updateAxesVisibility() {
  if (!this.chart || !this.chart.options || !this.chart.options.scales) return

  // Conjunto de eixos atualmente em uso (apenas datasets visíveis)
  const inUse = new Set(
    (this.chart.data.datasets || []).map(ds => String(ds.yAxisID || "1"))
  )

  // Percorre os eixos definidos no axisMeta (1..4) e liga/desliga a visibilidade
  Object.keys(this.axisMeta).forEach((axisId) => {
    const scale = this.chart.options.scales[axisId]
    if (!scale) return

    const shouldDisplay = inUse.has(axisId)
    scale.display = shouldDisplay

    // Opcional: esconder também as linhas de grade quando o eixo estiver oculto
    if (scale.grid) {
      scale.grid.display = shouldDisplay
    }
  })
}


  // Classes utilitárias para estado ativo/inativo (Tailwind-like)
  buttonClass(active) {
    const base =
      "border border-gray-200 rounded-lg mb-1 px-2 py-1 text-xs cursor-pointer select-none transition"
    const inactive = "hover:bg-gray-100 text-gray-700"
    const activeCls = "bg-enercon-green-1 text-white"
    return `${base} ${active ? activeCls : inactive}`.trim()
  }

  // Formatação numérica local (pt-BR). Ajuste se precisar outro locale.
  formatNumber(value) {
    try {
      return new Intl.NumberFormat("pt-BR", {
        maximumFractionDigits: 2
      }).format(value)
    } catch {
      return String(value)
    }
  }
}
